<template>
    <div class="tasks-table">
        <div class="mt-table">
            <header>
                <FilterGroup :facets="facets" v-model="criteria" />
                <div v-if="hasSelection" class="selection-actions">
                    <h3>
                        <span>{{ selectedItems.length }} Tasks Selected</span>
                    </h3>
                    <button class="button danger" @click="ignoreSelectedItems">Ignore Selected Tasks</button>
                </div>
                <h3 v-else class="task-count">{{ tasks.length }} Tasks</h3>
                <Paginator
                    :currentPage.sync="paginated.page"
                    :pageSize="paginated.pageSize"
                    :itemCount="paginated.count"
                    auto-hide
                />
            </header>
            <table v-if="paginated.count > 0">
                <thead>
                    <tr>
                        <CheckboxColumnHeader
                            :modelValue.sync="selectedItems"
                            :values="selectable"
                            class="checkbox"
                            v-if="selectable.length > 0"
                        />
                        <th class="checkbox" v-else>&nbsp;</th>
                        <SortableColumnHeader
                            v-if="showEntity"
                            label="Entity"
                            field="entity.title"
                            :sort.sync="sorted.sort"
                        />
                        <SortableColumnHeader label="Created" field="createdDate" :sort.sync="sorted.sort" />
                        <SortableColumnHeader label="Category" field="category" :sort.sync="sorted.sort" />
                        <SortableColumnHeader label="Description" field="description" :sort.sync="sorted.sort" />
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="task in paginated.results"
                        :key="task.id"
                        @click="() => taskRowClicked(task)"
                        :class="{ highlighted: currentTaskId === task.id }"
                    >
                        <td class="checkbox">
                            <input
                                type="checkbox"
                                v-model="selectedItems"
                                :value="task"
                                :disabled="rowDisabled(task)"
                                @click.stop
                            />
                        </td>
                        <td v-if="showEntity" data-label="Entity">
                            <div class="task-entity" v-if="task.entity">
                                <SearchResultAvatar :entity="task.entity" size="medium" />
                                <div>
                                    <router-link class="primary-link" :to="task.entity.link">
                                        {{ task.entity.title }}
                                    </router-link>
                                    <div>{{ task.entity.subtitle }}</div>
                                </div>
                            </div>
                        </td>
                        <td data-label="Created">{{ iso8601Date(task.createdDate) }}</td>
                        <td data-label="Category">{{ task.category }}</td>
                        <td data-label="Description">{{ task.description }}</td>
                    </tr>
                </tbody>
            </table>

            <div class="empty-block" v-else>
                <p>Up-to-date! There are currently no open tasks.</p>
            </div>
            <footer>
                <Paginator
                    :currentPage.sync="paginated.page"
                    :pageSize="paginated.pageSize"
                    :itemCount="paginated.count"
                    auto-hide
                />
            </footer>
        </div>
    </div>
</template>

<script setup>
import { iso8601Date } from '@/utils/format'
import FilterGroup from '@/components/filters/filter-group.vue'
import SearchResultAvatar from '@/components/Search/avatar'
import { ProviderJobService } from '@/services/manage/ProviderJobService'
import { ProviderJobTitleService } from '@/services/manage/ProviderJobTitleService'
import { ProviderJobRoleService } from '@/services/manage/ProviderJobRoleService'
import { computed, inject, onMounted, onUnmounted, ref } from 'vue'
import { sortable } from '@/utils/sortable'
import { paginate } from '@/utils/paginate'
import Paginator from '@/components/shared/paginator.vue'
import CheckboxColumnHeader from '@/pages/Research/CheckboxColumnHeader.vue'
import SortableColumnHeader from '@/components/shared/mt-table/SortableColumnHeader.vue'

const ignoreHandlers = {
    Jobs: function (item) {
        switch (item.secondaryEntityType) {
            case 'ProviderJob':
                return ProviderJobService.ignore(item.secondaryEntityId)
            case 'ProviderJobTitle':
                return ProviderJobTitleService.ignore(item.secondaryEntityId)
            case 'ProviderJobRole':
                return ProviderJobRoleService.ignore(item.secondaryEntityId)
        }
    },
}

const canBeIgnored = new Set(Object.keys(ignoreHandlers))

const props = defineProps({
    categories: {
        type: Array,
        required: true,
    },
    selectedCategories: {
        type: Array,
        required: true,
    },
    showEntity: {
        type: Boolean,
        default: true,
    },
    tasks: {
        type: Array,
        required: true,
    },
    updating: {
        type: Boolean,
        default: false,
    },
})

const emit = defineEmits(['update:selectedCategories', 'taskSelected', 'update'])

const selectedItems = ref([])
const sorted = sortable(() => props.tasks)
const paginated = paginate(() => sorted.results)
const selectable = computed(() => paginated.results.filter(t => canBeIgnored.has(t.category)))

const criteria = computed({
    get() {
        return props.selectedCategories.map(c => ({ name: 'Category', display: c, value: c }))
    },
    set(criteria) {
        emit('update:selectedCategories', [...new Set(criteria.map(c => c.value))])
    },
})

const facets = computed(() => [
    {
        name: 'Category',
        title: 'Category',
        type: 'checkbox',
        selectAllText: 'All Categories',
        values: props.categories.map(c => ({ display: c, value: c })),
    },
])

const hasSelection = computed(() => selectedItems.value.length > 0)

const store = inject('store')

const currentTaskId = computed(() => store.getters.currentTaskItem?.id ?? 0)

function rowDisabled(row) {
    return !canBeIgnored.has(row.category)
}

const flash = inject('flash')

async function ignoreSelectedItems() {
    await Promise.all(
        selectedItems.value.map(item => {
            let handler = ignoreHandlers[item.category]

            if (handler === undefined) {
                return Promise.resolve()
            }

            return handler(item)
        })
    )

    flash('Successfully ignored selected items.', 'success')

    emit('update')
    selectedItems.value = []
}

function taskRowClicked(task) {
    emit('taskSelected', task)
}

function taskCompleted(task) {
    if (sorted.results.length === 0) {
        return
    }

    const index = sorted.results.findIndex(t => t.id === task.id) + 1

    if (index < sorted.results.length) {
        taskRowClicked(sorted.results[index])
    }
}

const bus = inject('bus')
onMounted(() => bus.subscribe('task-completed', taskCompleted))
onUnmounted(() => bus.unsubscribe('task-completed', taskCompleted))
</script>

<style scoped>
.tasks-table {
    margin-top: 24px;
    height: 100%;
}

header {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
}

footer {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    padding: 8px 0;
}

.filter-group {
    width: 100%;
}

.paginator {
    float: none;
}

h3 {
    line-height: 64px;
}

.selection-actions {
    align-items: center;
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    gap: 24px;
}

button {
    width: auto;
}

table {
    table-layout: fixed;
}

.mt-table th.checkbox,
.mt-table td.checkbox {
    width: 48px;
}

.mt-table th.checkbox :deep(input[type='checkbox']) {
    position: static;
}

[data-label='Entity'] {
    width: 30%;

    .task-entity {
        display: flex;
        flex-direction: row;
        align-items: center;
    }
}

[data-label='Created'] {
    white-space: nowrap;
    width: 108px;
}

[data-label='Category'] {
    width: 160px;
}

[data-label='Description'] {
    width: 70%;
}

.primary-link {
    white-space: nowrap;
}
</style>
